// src/components/ListingItem.jsx
import React, { useMemo } from "react";
import { FaHeart, FaRegHeart } from "react-icons/fa";
import { Chip } from "@mui/material";

// ✅ One true ID, matching the ZillowClonePage logic
const getDocId = (l) =>
  String(l?.id ?? l?.property_id ?? l?.listing_id ?? l?.zpid ?? "");

export default function ListingItem({
  listing,
  onClick,
  mortgage1,
  // controlled favorites from parent:
  isFavorite = false,
  // parent persists to Firestore/localStorage (and does optimistic update)
  onToggleFavorite = () => {},
}) {
  const isOffMarket = !listing?.formattedAddress;

  // 🔹 Type flags derived from status (compatible with your mapper)
  const status = String(listing?.status || "").toLowerCase();
  const isForRent = /rent/.test(status);              // "For Rent", "for_rent", etc.
  const isSold    = /sold/.test(status) || listing?.listingMode === "Sold";

  // Simple image source (exactly as you had it so high-res stays)
  const imageSrc = listing?.imgSrc || listing?.image || "";

  // On-market cashflow logic
  const { FMR, cashflow, cashflowColor } = useMemo(() => {
    if (isOffMarket) return { FMR: 0, cashflow: 0, cashflowColor: "" };
    const tiers = [0, 1207, 1355, 1781, 2086];
    const idx = (listing?.bedrooms || 0) > 3 ? 4 : (listing?.bedrooms || 0);
    const fmr = tiers[idx] || 0;
    const pmt = Math.round(mortgage1 || 0);
    const cf = fmr - pmt;
    const color = cf < 1 ? "#FECACA" : cf < 200 ? "#FEF3C7" : "#BBF7D0";
    return { FMR: fmr, cashflow: cf, cashflowColor: color };
  }, [isOffMarket, listing?.bedrooms, mortgage1]);

  // Title, price, badge, subtext, chips & meta for each case
  const title = isOffMarket
    ? listing?.address || `${listing?.county || ""}${listing?.city ? `, ${listing.city}` : ""}`
    : listing?.formattedAddress;

  // Price text — add "/mo" for rent; if sold and a soldPrice exists, show it
  const priceText = (() => {
    if (isOffMarket) {
      return typeof listing?.price === "number"
        ? `$${Number(listing.price).toLocaleString()}`
        : listing?.price || "";
    }
    if (isSold) {
      const sold = listing?.soldPrice ?? listing?.price;
      return sold != null ? `$${Number(sold).toLocaleString()}` : "Sold";
    }
    if (isForRent) {
      return `$${Number(listing?.price || 0).toLocaleString()}/mo`;
    }
    return `$${Number(listing?.price || 0).toLocaleString()}`;
  })();

  // Badge — show "Sold" pill if sold; otherwise your days-on site badge
  const badge = isOffMarket
    ? listing?.dateRef || ""
    : isSold
      ? "Sold"
      : `${listing?.daysOnMarket ?? 0} days on Elevate`;

  const subtext = isOffMarket ? "" : `FMR $${FMR} · Payment: $${Math.round(mortgage1 || 0)}`;

  const chips = isOffMarket
    ? [
        { label: listing?.arv || "", outlined: false },
        { label: listing?.squareFeet || "", outlined: false },
      ]
    : [
        { label: listing?.propertyType, outlined: true },
        {
          label: `Cashflow: $${cashflow}`,
          outlined: false,
          bg: cashflowColor,
          color: cashflow < 1 ? "#991B1B" : "#475569",
        },
      ];

  const metas = isOffMarket
    ? [listing?.beds, listing?.baths]
    : [
        listing?.bedrooms != null ? `${listing.bedrooms} bd` : null,
        listing?.bathrooms != null ? `${listing.bathrooms} ba` : null,
      ];

  // Shared inline styles
  const styles = {
    card: {
      display: "flex",
      flexDirection: "column",
      background: "#fff",
      borderRadius: 8,
      overflow: "hidden",
      boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
      cursor: "pointer",
      transition: "box-shadow .2s",
      margin: 12,
      width: 320,
      position: "relative",
    },
    imgWrap: { position: "relative" },
    img: {
      width: "100%",
      height: 180,
      objectFit: "cover",
      display: "block",
    },
    badge: {
      position: "absolute",
      top: 8,
      left: 8,
      background: isSold ? "rgba(220,38,38,0.90)" : "rgba(0,0,0,0.6)", // red pill for Sold
      color: "#fff",
      padding: "2px 8px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      zIndex: 1,
      textTransform: "uppercase",
      letterSpacing: 0.4,
    },
    favBtn: {
      position: "absolute",
      top: 8,
      right: 8,
      zIndex: 2,
      cursor: "pointer",
      background: "transparent",
      border: "none",
      padding: 4,
      lineHeight: 0,
    },
    body: {
      padding: 16,
      display: "flex",
      flexDirection: "column",
      flexGrow: 1,
    },
    title: {
      fontSize: 16,
      fontWeight: 600,
      margin: "0 0 8px",
      lineHeight: 1.3,
    },
    price: {
      fontSize: 18,
      fontWeight: "bold",
      margin: "0 0 8px",
      color: "#1F2937",
    },
    chips: {
      display: "flex",
      gap: 8,
      marginBottom: 8,
    },
    chip: {
      height: 28,
      fontSize: 12,
      fontWeight: 600,
      textTransform: "capitalize",
    },
    subtext: {
      fontSize: 14,
      color: "#4B5563",
      margin: "0 0 12px",
    },
    metaRow: {
      display: "flex",
      gap: 16,
      marginTop: "auto",
      fontSize: 14,
      color: "#374151",
      fontWeight: 500,
    },
  };

  const docId = getDocId(listing);

  return (
    <li
      style={styles.card}
      onClick={() => onClick?.(listing)}
      onMouseEnter={(e) =>
        (e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)")
      }
      onMouseLeave={(e) =>
        (e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.1)")
      }
    >
      {/* Image + overlays */}
      <div style={styles.imgWrap}>
        <img style={styles.img} src={imageSrc} alt="property" loading="lazy" />
        {badge && <div style={styles.badge}>{badge}</div>}

        {/* Real button so clicks don't bubble to card */}
        <button
          type="button"
          style={styles.favBtn}
          onClick={(e) => {
            e.stopPropagation();
            // tell parent to persist & optimistically update
            onToggleFavorite(!isFavorite, docId, listing);
          }}
          title={isFavorite ? "Unfavorite" : "Favorite"}
          aria-pressed={isFavorite}
          aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
        >
          {isFavorite ? (
            <FaHeart color="#EF4444" size={20} />
          ) : (
            <FaRegHeart color="#FFF" size={20} />
          )}
        </button>
      </div>

      {/* Body */}
      <div style={styles.body}>
        <div style={styles.title}>{title}</div>
        <div style={styles.price}>{priceText}</div>
        <div style={styles.chips}>
          {chips.map((c, i) => (
            <Chip
              key={i}
              label={c.label}
              variant={c.outlined ? "outlined" : "filled"}
              style={{
                ...styles.chip,
                backgroundColor: c.bg,
                color: c.color,
              }}
            />
          ))}
        </div>
        <div style={styles.subtext}>{subtext}</div>
        <div style={styles.metaRow}>
          {metas.map((m, i) => m && <span key={i}>{m}</span>)}
        </div>
      </div>
    </li>
  );
}